import React, { useEffect, useRef, useState, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const LIGHTNING_COLOR = '#AADDFF';  // 파란색 계열 번개 색상
const BACKGROUND_COLOR = '#061426';  // 어두운 밤하늘 색상
const GLOW_COLOR = 'rgba(90, 200, 255, 0.2)';  // 번개 주변 발광 효과
const STORM_CLOUDS = [
  { color: 'rgba(30, 35, 50, 0.9)', height: 0.35 },
  { color: 'rgba(40, 45, 60, 0.85)', height: 0.2 },
  { color: 'rgba(20, 25, 35, 0.8)', height: 0.5 }
];
const THUNDER_SOUNDS = [
  '/thunder1.mp3',
  '/thunder2.mp3',
  '/thunder3.mp3'
];

const LightningFractal = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [complexity, setComplexity] = useState(0.8); // 0.0 ~ 1.0 (번개 복잡도)
  const [lightningCount, setLightningCount] = useState(1); // 동시에 생성되는 번개 수
  const [frequency, setFrequency] = useState(2); // 번개 생성 빈도 (초당)
  const [withSound, setWithSound] = useState(true); // 소리 효과 활성화 여부
  
  // 마지막 번개 생성 시간 추적
  const lastLightningTime = useRef(0);
  // 날씨 상태 (비, 구름 등)
  const [weatherIntensity, setWeatherIntensity] = useState(0.7); // 0.0 ~ 1.0 (비/구름 강도)
  const [rainCount, setRainCount] = useState(100); // 빗방울 수
  const raindrops = useRef([]);
  
  // 구름 및 하늘 배경 그리기
  const drawBackground = useCallback((ctx) => {
    // 그라데이션 하늘 배경
    const skyGradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    skyGradient.addColorStop(0, '#030912');  // 어두운 상단
    skyGradient.addColorStop(1, '#081b30');  // 약간 밝은 하단
    
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 별 그리기 (하늘이 밝아지지 않았을 때만)
    const starCount = Math.floor(Math.random() * 30) + 20;
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    
    for (let i = 0; i < starCount; i++) {
      const x = Math.random() * CANVAS_WIDTH;
      const y = Math.random() * CANVAS_HEIGHT * 0.5;  // 상단 절반에만 별 그리기
      const size = Math.random() * 1.5;
      
      // 깜박이는 효과
      const flicker = Math.random() > 0.97;
      if (!flicker) {
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // 구름 그리기
    STORM_CLOUDS.forEach(cloud => {
      const cloudHeight = CANVAS_HEIGHT * cloud.height;
      
      // 구름 그라데이션
      const cloudGradient = ctx.createLinearGradient(0, cloudHeight - 150, 0, cloudHeight + 50);
      cloudGradient.addColorStop(0, 'rgba(20, 25, 35, 0)');  // 상단 투명
      cloudGradient.addColorStop(0.4, cloud.color);          // 중앙 구름 색상
      cloudGradient.addColorStop(1, 'rgba(30, 35, 50, 0)');  // 하단 투명
      
      ctx.fillStyle = cloudGradient;
      
      // 구름 형태를 위한 곡선 그리기
      ctx.beginPath();
      
      // 파동 모양의 구름 상단
      ctx.moveTo(0, cloudHeight);
      
      const cloudSegments = 12;
      const segmentWidth = CANVAS_WIDTH / cloudSegments;
      
      for (let i = 0; i <= cloudSegments; i++) {
        // 구름의 높낮이 변화 (약간의 랜덤성)
        const waveHeight = 20 + Math.sin(i * 0.5 + Date.now() * 0.0005) * 15;
        const x = i * segmentWidth;
        const y = cloudHeight - waveHeight * (weatherIntensity * 0.8 + 0.2);
        
        if (i === 0) {
          ctx.lineTo(x, y);
        } else {
          // 부드러운 곡선을 위한 제어점
          const cpX = x - segmentWidth / 2;
          const cpY = cloudHeight - Math.sin((i - 0.5) * 0.5 + Date.now() * 0.0005) * 15 * (weatherIntensity * 0.8 + 0.2);
          ctx.quadraticCurveTo(cpX, cpY, x, y);
        }
      }
      
      // 구름 바닥 부분 연결하여 닫기
      ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.lineTo(0, CANVAS_HEIGHT);
      ctx.closePath();
      ctx.fill();
    });
    
    // 희미한 번개 반사광 (간헐적)
    if (Math.random() > 0.85) {
      const intensity = Math.random() * 0.15;
      ctx.fillStyle = `rgba(120, 190, 255, ${intensity})`;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }
  }, [weatherIntensity]);
  
  // 번개 분기 생성 함수
  const createLightningBranch = useCallback((ctx, startX, startY, angle, length, thickness, generation = 0, complexityFactor) => {
    if (length < 2 || generation > 13 || thickness < 0.5) return;
    
    // 번개 끝점 계산
    const endX = startX + Math.cos(angle) * length;
    const endY = startY + Math.sin(angle) * length;
    
    // 번개 진행 경로에 약간의 지그재그 효과 추가
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    
    // 지그재그 효과 강도 (낮은 세대에서는 더 크게)
    const offsetRange = length * (0.2 - generation * 0.01);
    
    // 랜덤 각도로 지그재그 오프셋 적용
    const offsetAngle = angle + Math.PI / 2;
    const offsetX = midX + Math.cos(offsetAngle) * (Math.random() * offsetRange * 2 - offsetRange);
    const offsetY = midY + Math.sin(offsetAngle) * (Math.random() * offsetRange * 2 - offsetRange);
    
    // 번개 경로 그리기
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.quadraticCurveTo(offsetX, offsetY, endX, endY);
    
    // 번개 두께 및 색상 설정
    ctx.lineWidth = thickness;
    
    // 발광 효과
    ctx.shadowBlur = thickness * 3;
    ctx.shadowColor = LIGHTNING_COLOR;
    
    // 번개 중심부 (밝은 파란색-흰색)
    ctx.strokeStyle = 'rgba(210, 245, 255, 0.9)';
    ctx.stroke();
    
    // 번개 바깥 발광 효과 (흐릿한 파란색)
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.quadraticCurveTo(offsetX, offsetY, endX, endY);
    ctx.lineWidth = thickness * 2.5;
    ctx.strokeStyle = 'rgba(120, 180, 255, 0.3)';
    ctx.shadowBlur = thickness * 6;
    ctx.stroke();
    
    // 랜덤 분기 생성 (복잡도에 따라 분기 확률 조정)
    const branchProbability = Math.min(0.5, complexityFactor * (0.5 - generation * 0.04));
    
    if (Math.random() < branchProbability && length > 10) {
      // 분기 각도 및 길이 결정
      const branchAngle = angle + (Math.random() * Math.PI / 4 - Math.PI / 8);
      const branchLength = length * (0.5 + Math.random() * 0.3);
      const branchThickness = thickness * 0.7;
      
      // 재귀적으로 분기 생성
      createLightningBranch(
        ctx, 
        offsetX, 
        offsetY, 
        branchAngle, 
        branchLength, 
        branchThickness, 
        generation + 1,
        complexityFactor
      );
    }
    
    // 다음 세그먼트 (줄기 계속)
    if (generation < 4 || Math.random() < 0.7) {
      // 약간 방향 변경 (지그재그)
      const newAngle = angle + (Math.random() * Math.PI / 8 - Math.PI / 16);
      const newLength = length * 0.8;
      const newThickness = thickness * 0.9;
      
      // 재귀적으로 번개 계속 생성
      createLightningBranch(
        ctx, 
        endX, 
        endY, 
        newAngle, 
        newLength, 
        newThickness, 
        generation + 1,
        complexityFactor
      );
    }
  }, []);
  
  // 번개 생성 함수
  const createLightning = useCallback((ctx) => {
    // 번개 시작 지점 (상단 또는 구름 내부)
    const startX = Math.random() * CANVAS_WIDTH;
    const startY = Math.random() * CANVAS_HEIGHT * 0.3;
    
    // 기본 진행 방향 (아래쪽으로)
    const mainAngle = Math.PI / 2 + (Math.random() * 0.4 - 0.2);
    
    // 번개 길이 및 두께
    const length = CANVAS_HEIGHT * (0.3 + Math.random() * 0.3);
    const thickness = 2 + Math.random() * 2;
    
    // 번개 생성 (복잡도에 따라 분기 확률 조정)
    createLightningBranch(ctx, startX, startY, mainAngle, length, thickness, 0, complexity);
    
    // 번개 주변 발광 효과
    ctx.fillStyle = GLOW_COLOR;
    ctx.filter = 'blur(30px)';
    ctx.beginPath();
    ctx.arc(startX, startY + length * 0.5, length * 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.filter = 'none';
    
    // 추가 효과: 번개 치는 소리 (소리 효과가 활성화 되어 있으면)
    if (withSound && audioRef.current) {
      // 음원 선택 (여러 소리 중 하나)
      const randomSound = THUNDER_SOUNDS[Math.floor(Math.random() * THUNDER_SOUNDS.length)];
      
      try {
        // 이미 재생 중인 경우 정지
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        
        // 새 소리 설정 및 재생
        audioRef.current.src = randomSound;
        const playPromise = audioRef.current.play();
        
        // 브라우저 자동 재생 정책 처리
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Thunder sound auto-play prevented:', error);
          });
        }
      } catch (error) {
        console.log('Thunder sound error:', error);
      }
    }
  }, [createLightningBranch, complexity, withSound]);
  
  // 비 효과 초기화
  const initRain = useCallback(() => {
    const drops = [];
    
    for (let i = 0; i < rainCount; i++) {
      drops.push({
        x: Math.random() * CANVAS_WIDTH,
        y: Math.random() * CANVAS_HEIGHT,
        length: Math.random() * 10 + 5,  // 빗방울 길이
        speed: Math.random() * 10 + 15   // 빗방울 속도
      });
    }
    
    raindrops.current = drops;
  }, [rainCount]);
  
  // 비 효과 업데이트 및 그리기
  const updateAndDrawRain = useCallback((ctx) => {
    if (weatherIntensity < 0.3) return; // 날씨 강도가 낮으면 비 효과 생략
    
    const intensity = (weatherIntensity - 0.3) / 0.7; // 0.3~1.0 범위를 0~1로 정규화
    
    ctx.strokeStyle = 'rgba(180, 220, 255, ' + (0.3 * intensity) + ')';
    ctx.lineWidth = 1;
    
    // 각 빗방울 업데이트 및 그리기
    raindrops.current.forEach((drop, index) => {
      // 빗방울이 캔버스 밖으로 나가면 재설정
      if (drop.y > CANVAS_HEIGHT) {
        drop.y = -drop.length;
        drop.x = Math.random() * CANVAS_WIDTH;
      }
      
      // 이동
      drop.y += drop.speed;
      
      // 그리기
      ctx.beginPath();
      ctx.moveTo(drop.x, drop.y);
      ctx.lineTo(drop.x, drop.y + drop.length);
      ctx.stroke();
    });
  }, [weatherIntensity]);
  
  // 애니메이션 프레임 업데이트
  const update = useCallback(() => {
    if (!isPlaying) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // 배경 지우기
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 배경 (하늘, 구름 등) 그리기
    drawBackground(ctx);
    
    // 빗방울 업데이트 및 그리기
    updateAndDrawRain(ctx);
    
    // 현재 시간
    const now = Date.now();
    
    // 번개 생성 간격 계산 (빈도에 따라 조정, 랜덤 요소 추가)
    const interval = (1000 / frequency) * (1 + Math.random() * 0.5);
    
    // 시간 간격에 따라 새 번개 생성
    if (now - lastLightningTime.current > interval) {
      lastLightningTime.current = now;
      
      // 여러 번개 생성 (lightningCount에 따라)
      for (let i = 0; i < lightningCount; i++) {
        // 약간의 시간차로 번개 생성 (보다 자연스러운 효과)
        setTimeout(() => {
          createLightning(ctx);
        }, i * 100);
      }
    }
    
    // 다음 프레임 요청
    animationRef.current = requestAnimationFrame(update);
  }, [drawBackground, updateAndDrawRain, createLightning, isPlaying, frequency, lightningCount]);
  
  // 재생/일시 정지 토글
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };
  
  // 복잡도 변경 핸들러
  const handleComplexityChange = (e) => {
    setComplexity(parseFloat(e.target.value));
  };
  
  // 번개 수 변경 핸들러
  const handleLightningCountChange = (e) => {
    setLightningCount(parseInt(e.target.value));
  };
  
  // 빈도 변경 핸들러
  const handleFrequencyChange = (e) => {
    setFrequency(parseFloat(e.target.value));
  };
  
  // 날씨 강도 변경 핸들러
  const handleWeatherChange = (e) => {
    setWeatherIntensity(parseFloat(e.target.value));
    
    // 비 효과 업데이트
    const intensity = parseFloat(e.target.value);
    setRainCount(Math.floor(intensity * 200));
    initRain();
  };
  
  // 소리 효과 토글
  const toggleSound = () => {
    setWithSound(!withSound);
  };
  
  // 초기화 및 정리
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    // 비 효과 초기화
    initRain();
    
    // 애니메이션 시작
    animationRef.current = requestAnimationFrame(update);
    
    // 오디오 요소 생성 (소리 효과용)
    audioRef.current = new Audio();
    audioRef.current.volume = 0.5;
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      // 오디오 정리
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [update, initRain]);
  
  // isPlaying 상태 변경 시 업데이트
  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(update);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, [isPlaying, update]);
  
  // 날씨 변화에 따른 비 효과 업데이트
  useEffect(() => {
    initRain();
  }, [rainCount, initRain]);
  
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Lightning Fractal Storm</h2>
      <div style={canvasContainerStyle}>
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={canvasStyle} />
      </div>
      
      <div style={controlsStyle}>
        <div style={controlGroupStyle}>
          <label htmlFor="complexity" style={labelStyle}>번개 복잡도: {(complexity * 100).toFixed(0)}%</label>
          <input
            id="complexity"
            type="range"
            min="0.2"
            max="1.0"
            step="0.05"
            value={complexity}
            onChange={handleComplexityChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="lightningCount" style={labelStyle}>번개 수: {lightningCount}</label>
          <input
            id="lightningCount"
            type="range"
            min="1"
            max="5"
            step="1"
            value={lightningCount}
            onChange={handleLightningCountChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="frequency" style={labelStyle}>번개 빈도: {frequency.toFixed(1)}/초</label>
          <input
            id="frequency"
            type="range"
            min="0.2"
            max="5.0"
            step="0.1"
            value={frequency}
            onChange={handleFrequencyChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="weather" style={labelStyle}>폭풍 강도: {(weatherIntensity * 100).toFixed(0)}%</label>
          <input
            id="weather"
            type="range"
            min="0.1"
            max="1.0"
            step="0.05"
            value={weatherIntensity}
            onChange={handleWeatherChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={buttonContainerStyle}>
          <button
            onClick={togglePlay}
            style={isPlaying ? pauseButtonStyle : playButtonStyle}
          >
            {isPlaying ? '일시 정지' : '재생'}
          </button>
          
          <button
            onClick={toggleSound}
            style={{
              ...buttonBaseStyle,
              background: withSound
                ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)'
                : 'linear-gradient(135deg, #7f8c8d 0%, #95a5a6 100%)'
            }}
          >
            {withSound ? '소리 켜짐' : '소리 꺼짐'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Styled Components ---
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0c1626 0%, #1a1a2e 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  padding: '0.5rem',
  background: 'rgba(0,0,0,0.4)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(35, 45, 65, 0.5) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const controlsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
  marginTop: '1.5rem',
  width: '80%',
  maxWidth: '500px',
  background: 'rgba(10, 20, 35, 0.7)',
  padding: '1.5rem',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
};

const controlGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#aaddff'
};

const sliderStyle = {
  width: '100%',
  height: '8px',
  appearance: 'none',
  background: 'linear-gradient(to right, #1a103f, #2980b9)',
  outline: 'none',
  borderRadius: '10px',
  cursor: 'pointer'
};

const buttonContainerStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '0.5rem'
};

const buttonBaseStyle = {
  flex: 1,
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.2)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  outline: 'none'
};

const playButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
  color: 'white'
};

const pauseButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
  color: 'white'
};

export default LightningFractal; 